import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
} from 'react';

import { cn } from './cn.ts';

const CONTROL =
  'w-full rounded-lg border border-border bg-surface-sunken px-3 text-sm text-fg placeholder:text-fg-subtle ' +
  'transition focus:border-accent focus:outline-none focus-visible:outline-2 focus-visible:outline-focus ' +
  'disabled:opacity-60 aria-[invalid=true]:border-danger';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(CONTROL, 'h-10', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cn(CONTROL, 'h-9 pr-8', className)} {...rest}>
        {children}
      </select>
    );
  },
);

export interface FieldProps {
  label: ReactNode;
  /** Shown under the control; for guidance, not errors. */
  hint?: ReactNode;
  /** Shown instead of the hint, announced to screen readers. */
  error?: string | undefined;
  /** The control: an Input, Select or anything accepting id/aria props. */
  children: ReactNode;
  className?: string;
}

/**
 * A labelled form control with its hint and error wired up for assistive technology:
 * the label names the control, and the hint or error describes it.
 */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const describedBy = error || hint ? `${id}-description` : undefined;
  const control = isValidElement<Record<string, unknown>>(children)
    ? cloneElement(children, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })
    : children;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {label}
      </label>
      {control}
      {error ? (
        <p id={describedBy} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={describedBy} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
